<template>
  <div
      class="ola-carousel"
      :class="{ 'ola-carousel-vertical': direction === 'vertical' }"
      :style="carouselStyle"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
  >
    <div
        class="ola-carousel-container"
        :style="containerStyle"
    >
      <slot></slot>
    </div>

    <!-- 指示器 -->
    <ul
        v-if="indicatorPosition !== 'none' && items.length > 1"
        class="ola-carousel-indicators"
        :class="[
        `ola-carousel-indicators-${direction}`,
        `ola-carousel-indicators-${indicatorPosition}`,
        { 'ola-carousel-indicators-dots': indicatorType === 'dots' }
      ]"
    >
      <li
          v-for="(item, index) in items"
          :key="index"
          class="ola-carousel-indicator"
          :class="{ 'ola-carousel-indicator-active': index === activeIndex }"
          @click="setActiveItem(index)"
      >
        <button class="ola-carousel-button"></button>
      </li>
    </ul>

    <!-- 箭头 -->
    <button
        v-if="(arrow === 'always' || (arrow === 'hover' && showArrow)) && items.length > 1"
        class="ola-carousel-arrow ola-carousel-arrow-left"
        @click.prevent.stop="prev"
        @mouseenter="handleArrowMouseEnter"
        @mouseleave="handleArrowMouseLeave"
        type="button"
    >
      <span class="arrow-icon">&lt;</span>
    </button>
    <button
        v-if="(arrow === 'always' || (arrow === 'hover' && showArrow)) && items.length > 1"
        class="ola-carousel-arrow ola-carousel-arrow-right"
        @click.prevent.stop="next"
        @mouseenter="handleArrowMouseEnter"
        @mouseleave="handleArrowMouseLeave"
        type="button"
    >
      <span class="arrow-icon">&gt;</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'OlaCarousel',
  props: {
    // 高度
    height: {
      type: String,
      default: '300px'
    },
    // 平板设备高度
    tabletHeight: {
      type: String,
      default: ''
    },
    // 移动设备高度
    mobileHeight: {
      type: String,
      default: ''
    },
    // 初始索引
    initialIndex: {
      type: Number,
      default: 0
    },
    // 是否自动播放
    autoplay: {
      type: Boolean,
      default: false
    },
    // 自动播放间隔，单位为毫秒
    interval: {
      type: Number,
      default: 3000
    },
    // 指示器位置：inside, outside, none
    indicatorPosition: {
      type: String,
      default: 'inside',
      validator: (value) => {
        return ['inside', 'outside', 'none'].includes(value);
      }
    },
    // 指示器类型：line, dots
    indicatorType: {
      type: String,
      default: 'line',
      validator: (value) => {
        return ['line', 'dots'].includes(value);
      }
    },
    // 箭头显示时机：always, hover, never
    arrow: {
      type: String,
      default: 'hover',
      validator: (value) => {
        return ['always', 'hover', 'never'].includes(value);
      }
    },
    // 轮播方向：horizontal, vertical
    direction: {
      type: String,
      default: 'horizontal',
      validator: (value) => {
        return ['horizontal', 'vertical'].includes(value);
      }
    },
    // 循环播放
    loop: {
      type: Boolean,
      default: true
    },
    // 轮播类型：default, card
    type: {
      type: String,
      default: 'default',
      validator: (value) => {
        return ['default', 'card'].includes(value);
      }
    }
  },
  emits: ['change'],
  provide() {
    return {
      carousel: this
    };
  },
  data() {
    return {
      items: [],
      activeIndex: -1,
      timer: null,
      hover: false,
      showArrow: false,
      arrowHover: false,
      isInitialized: false,
      windowWidth: 1200 // 默认桌面宽度，避免SSR错误
    };
  },
  computed: {
    // 容器样式
    containerStyle() {
      const style = {};
      if (this.direction === 'horizontal') {
        style.transform = `translateX(-${this.activeIndex * 100}%)`;
      } else {
        style.transform = `translateY(-${this.activeIndex * 100}%)`;
      }
      style.transition = 'transform .5s ease-in-out';
      return style;
    },
    // 轮播图样式，包含响应式高度
    carouselStyle() {
      const style = {};

      // 根据屏幕宽度设置高度
      if (this.windowWidth <= 576) { // 移动设备
        style.height = this.mobileHeight || this.tabletHeight || this.height;
      } else if (this.windowWidth <= 1025) { // 平板设备
        style.height = this.tabletHeight || this.height;
      } else { // 桌面设备
        style.height = this.height;
      }

      return style;
    }
  },
  watch: {
    // 监听自动播放状态变化
    activeIndex(val, oldVal) {
      this.resetTimer();
      this.$emit('change', val);

      // 更新轮播项的活动状态
      if (this.items.length > 0) {
        if (oldVal >= 0 && oldVal < this.items.length) {
          this.items[oldVal].active = false;
        }
        if (val >= 0 && val < this.items.length) {
          this.items[val].active = true;
        }
      }
    },
    autoplay(val) {
      val ? this.startTimer() : this.pauseTimer();
    },
    interval() {
      this.resetTimer();
    },
    hover(val) {
      if (!this.arrowHover) {
        this.showArrow = val;
      }
      if (this.autoplay) {
        val ? this.pauseTimer() : this.startTimer();
      }
    },
    arrowHover(val) {
      if (val) {
        this.showArrow = true;
      } else if (!this.hover) {
        this.showArrow = false;
      }
    }
  },
  methods: {
    // 添加轮播项
    addItem(item) {
      this.items.push(item);

      // 如果已经初始化并且是第一个项，则设置为活动状态
      if (this.isInitialized && this.items.length === 1) {
        this.activeIndex = 0;
        item.active = true;
      }
    },
    // 移除轮播项
    removeItem(item) {
      const index = this.items.indexOf(item);
      if (index > -1) {
        this.items.splice(index, 1);
      }
    },
    // 设置激活的轮播项
    setActiveItem(index) {
      if (index < 0) {
        if (this.loop) {
          this.activeIndex = this.items.length - 1;
        } else {
          this.activeIndex = 0;
        }
      } else if (index >= this.items.length) {
        if (this.loop) {
          this.activeIndex = 0;
        } else {
          this.activeIndex = this.items.length - 1;
        }
      } else {
        this.activeIndex = index;
      }
    },
    // 上一项
    prev() {
      this.setActiveItem(this.activeIndex - 1);
    },
    // 下一项
    next() {
      this.setActiveItem(this.activeIndex + 1);
    },
    // 开始自动播放
    startTimer() {
      if (this.interval <= 0 || !this.autoplay) return;
      this.timer = setInterval(() => {
        this.next();
      }, this.interval);
    },
    // 暂停自动播放
    pauseTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    // 重置自动播放
    resetTimer() {
      this.pauseTimer();
      this.startTimer();
    },
    // 鼠标进入
    handleMouseEnter() {
      this.hover = true;
    },
    // 鼠标离开
    handleMouseLeave() {
      this.hover = false;
    },
    // 箭头鼠标进入
    handleArrowMouseEnter() {
      this.arrowHover = true;
    },
    // 箭头鼠标离开
    handleArrowMouseLeave() {
      this.arrowHover = false;
    },
    // 处理窗口大小变化
    handleWindowResize() {
      this.windowWidth = window.innerWidth;
      // 在下一帧重新计算样式
      this.$nextTick(() => {
        // this.resetItemPosition();
      });
    },
    // 初始化客户端特定功能
    initClientFeatures() {
      if (typeof window !== 'undefined') {
        // 在客户端环境中获取窗口宽度
        this.windowWidth = window.innerWidth;

        // 添加窗口大小改变事件监听
        window.addEventListener('resize', this.handleWindowResize);
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      if (this.initialIndex >= 0 && this.initialIndex < this.items.length) {
        this.activeIndex = this.initialIndex;
      } else {
        this.activeIndex = 0;
      }

      // 确保初始活动项状态正确
      if (this.activeIndex >= 0 && this.activeIndex < this.items.length) {
        this.items[this.activeIndex].active = true;
      }

      this.isInitialized = true;
      this.startTimer();

      // 初始化客户端特定功能
      this.initClientFeatures();

      // 初始获取窗口宽度
      this.windowWidth = window.innerWidth;

      // 重置项目位置
      //this.resetItemPosition();

      // 添加窗口大小变化监听
      window.addEventListener('resize', this.handleWindowResize);
    });
  },
  beforeUnmount() {
    this.pauseTimer();

    // 移除窗口大小变化监听
    window.removeEventListener('resize', this.handleWindowResize);
  }
};
</script>

<style lang="scss">
.ola-carousel {
  position: relative;
  overflow: hidden;
  width: 100%;
}

.ola-carousel-container {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  transition: transform .5s ease-in-out;
}



.ola-carousel-vertical .ola-carousel-container {
  flex-direction: column;
}

/* 指示器 */
.ola-carousel-indicators {
  position: absolute;
  list-style: none;
  margin: 0;
  padding: 0;
  z-index: 2;
  display: flex;
}

.ola-carousel-indicators-horizontal {
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
}

.ola-carousel-indicators-vertical {
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  flex-direction: column;
}

.ola-carousel-indicators-outside {
  bottom: -30px;
}

.ola-carousel-indicator {
  display: inline-block;
  padding: 12px 4px;
  cursor: pointer;
}

@media (max-width: 767px) {
  .ola-carousel-indicators-horizontal {
    bottom: 5px;
  }

  .ola-carousel-indicator {
    padding: 8px 3px;
  }
}

@media (max-width: 576px) {
  .ola-carousel-indicators-horizontal {
    bottom: 3px;
  }

  .ola-carousel-indicator {
    padding: 6px 2px;
  }
}

.ola-carousel-button {
  display: block;
  opacity: 0.48;
  width: 30px;
  height: 2px;
  background-color: #fff;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  transition: .3s;
}

@media (max-width: 767px) {
  .ola-carousel-button {
    width: 20px;
  }
}

@media (max-width: 576px) {
  .ola-carousel-button {
    width: 15px;
  }
}

.ola-carousel-indicators-dots .ola-carousel-button {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

@media (max-width: 576px) {
  .ola-carousel-indicators-dots .ola-carousel-button {
    width: 8px;
    height: 8px;
  }
}

.ola-carousel-indicator-active .ola-carousel-button {
  opacity: 1;
}

.ola-carousel-indicators-vertical .ola-carousel-button {
  width: 2px;
  height: 30px;
}

.ola-carousel-indicators-vertical.ola-carousel-indicators-dots .ola-carousel-button {
  width: 10px;
  height: 10px;
}

/* 箭头 */
.ola-carousel-arrow {
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  height: 36px;
  width: 36px;
  cursor: pointer;
  transition: opacity .3s ease, background-color .3s ease;
  border-radius: 50%;
  background-color: rgba(31, 45, 61, 0.5);
  color: #fff;
  position: absolute;
  top: 50%;
  z-index: 10;
  transform: translateY(-50%);
  text-align: center;
  font-size: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  -webkit-tap-highlight-color: transparent;
}

@media (max-width: 767px) {
  .ola-carousel-arrow {
    height: 30px;
    width: 30px;
    font-size: 16px;
  }

  .ola-carousel-arrow-left {
    left: 10px;
  }

  .ola-carousel-arrow-right {
    right: 10px;
  }
}

@media (max-width: 576px) {
  .ola-carousel-arrow {
    height: 26px;
    width: 26px;
    font-size: 14px;
  }

  .ola-carousel-arrow-left {
    left: 5px;
  }

  .ola-carousel-arrow-right {
    right: 5px;
  }
}

.ola-carousel-arrow .arrow-icon {
  font-weight: 500;
  line-height: 1;
}

.ola-carousel-arrow-left {
  left: 16px;
}

.ola-carousel-arrow-right {
  right: 16px;
}

.ola-carousel-arrow:hover {
  background-color: rgba(31, 45, 61, 0.7);
}

/* 响应式 */
@media (max-width: 992px) {
  .ola-carousel-arrow {
    height: 30px;
    width: 30px;
    font-size: 16px;
  }

  .ola-carousel-arrow-left {
    left: 12px;
  }

  .ola-carousel-arrow-right {
    right: 12px;
  }

  .ola-carousel-button {
    width: 25px;
  }

  .ola-carousel-indicators-vertical .ola-carousel-button {
    height: 25px;
  }
}

@media (max-width: 576px) {
  .ola-carousel-arrow {
    height: 24px;
    width: 24px;
    font-size: 14px;
  }

  .ola-carousel-arrow-left {
    left: 8px;
  }

  .ola-carousel-arrow-right {
    right: 8px;
  }

  .ola-carousel-button {
    width: 20px;
  }

  .ola-carousel-indicators-vertical .ola-carousel-button {
    height: 20px;
  }
}
</style>